import { createContext, useContext, useState, useEffect, useCallback, useRef, startTransition } from 'react';
import { trackEvent } from '../services/analyticsService';
import { loadLocaleTranslations } from '../services/translationService';
import { EVENTS } from '../utils/analyticsEvents';
import Loader from '../components/Loader';
import { SITE_CONFIG } from '../config/site';

const LOCALE_KEY = 'locale';
const SUPPORTED_LOCALES = SITE_CONFIG.locales;
const DEFAULT_LOCALE = SITE_CONFIG.defaultLocale;

function getPreferredLocale() {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const stored = localStorage.getItem(LOCALE_KEY);
  if (SUPPORTED_LOCALES.includes(stored)) return stored;
  const systemLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
  const match = SUPPORTED_LOCALES.find((lg) => systemLang.startsWith(lg));
  return match ?? DEFAULT_LOCALE;
}

const TranslationContext = createContext(null);

function resolveInitialData(initialTranslations) {
  if (!initialTranslations) return {};
  if (typeof initialTranslations.en === 'object' && initialTranslations.en !== null) {
    return { ...initialTranslations };
  }
  // SSR passes a single flattened locale (the default one).
  return { [DEFAULT_LOCALE]: initialTranslations };
}

// Locale state machine:
//   locale        - the ACTIVE locale; its copy is always in `data`, so the
//                   selector, body and PDF can never disagree.
//   pendingLocale - a locale whose chunk is being fetched; the UI keeps
//                   rendering the active one until it arrives.
//   failedLocale  - the last locale whose chunk failed to load; the active
//                   locale is untouched and the visitor can simply pick again.
// Hydration always starts in the default locale (matching the prerendered
// HTML); the visitor's stored/system preference is applied right after mount
// through the same load-then-commit path as a manual switch.
export function TranslationProvider({ children, initialTranslations }) {
  const [data, setData] = useState(() => resolveInitialData(initialTranslations));
  const [locale, setActiveLocale] = useState(DEFAULT_LOCALE);
  const [pendingLocale, setPendingLocale] = useState(null);
  const [failedLocale, setFailedLocale] = useState(null);
  const dataRef = useRef(data);
  dataRef.current = data;
  const requestId = useRef(0);
  const trackedLocale = useRef(DEFAULT_LOCALE);

  const commit = useCallback((value) => {
    setActiveLocale(value);
    if (typeof window !== 'undefined') localStorage.setItem(LOCALE_KEY, value);
  }, []);

  useEffect(() => {
    const previous = trackedLocale.current;
    if (previous !== locale) {
      trackEvent(EVENTS.LANGUAGE_SWITCH, { from: previous, to: locale });
      trackedLocale.current = locale;
    }
  }, [locale]);

  const setLocale = useCallback(
    (next) => {
      const value = SUPPORTED_LOCALES.includes(next) ? next : DEFAULT_LOCALE;
      setFailedLocale(null);
      if (dataRef.current[value]) {
        requestId.current += 1; // supersede any in-flight load
        setPendingLocale(null);
        commit(value);
        return;
      }
      const id = ++requestId.current;
      setPendingLocale(value);
      loadLocaleTranslations(value)
        .then((result) => {
          if (id !== requestId.current) return;
          // A stored locale can finish loading while a lazy route is still
          // hydrating. Mark the locale commit as a transition so React can
          // finish hydrating the Suspense boundary instead of replacing it.
          startTransition(() => {
            setData((prev) => ({ ...prev, [value]: result }));
            setPendingLocale(null);
            commit(value);
          });
        })
        .catch(() => {
          if (id !== requestId.current) return;
          startTransition(() => {
            setPendingLocale(null);
            setFailedLocale(value);
          });
        });
    },
    [commit]
  );

  // A failed dynamic import() is cached by the browser's module map, so calling
  // the same specifier again never re-fetches. Recovery therefore persists the
  // wanted locale and reloads: the fresh page adopts it through the normal path.
  const retryLocale = useCallback((value) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(LOCALE_KEY, value);
    window.location.reload();
  }, []);

  // Adopt the visitor's preference after hydration (load-then-commit).
  useEffect(() => {
    const preferred = getPreferredLocale();
    if (preferred !== DEFAULT_LOCALE) {
      startTransition(() => setLocale(preferred));
    }
  }, [setLocale]);

  // Non-prerendered entry without seeded copy: fetch the default locale.
  useEffect(() => {
    if (!dataRef.current[DEFAULT_LOCALE] && pendingLocale !== DEFAULT_LOCALE && failedLocale !== DEFAULT_LOCALE) {
      setLocale(DEFAULT_LOCALE);
    }
  }, [setLocale, pendingLocale, failedLocale]);

  const translations = data[locale] ?? data[DEFAULT_LOCALE];

  if (!translations) {
    if (failedLocale === DEFAULT_LOCALE) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-theme-page">
          <div className="text-theme-error text-lg">Failed to load translations</div>
          <button type="button" onClick={() => retryLocale(DEFAULT_LOCALE)} className="px-4 py-2 rounded-lg border border-theme-border text-theme-heading">
            Retry
          </button>
        </div>
      );
    }
    return <Loader fullScreen={true} message="Loading..." />;
  }

  return (
    <TranslationContext.Provider value={{ translations, locale, pendingLocale, failedLocale, setLocale, retryLocale }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslations() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslations must be used within TranslationProvider');
  }
  return context.translations;
}

export function useLocale() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useLocale must be used within TranslationProvider');
  }
  const { locale, pendingLocale, failedLocale, setLocale, retryLocale } = context;
  return { locale, pendingLocale, failedLocale, setLocale, retryLocale };
}
